import { useContext, useEffect, useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { uniqBy } from "lodash";

import { ActiveClientsInput } from "@src/types.generated";
import { ClientIdContext } from "@src/features/builders/shared/presence/ClientIdContext";
import { ActiveClientFragment } from "@src/lib/ui/ActiveClientsAvatarStack.generated";
import { usePollInterval } from "@src/lib/hooks/usePollInterval";

import { useActiveClientsQuery } from "./useActiveClientsCore.generated";

const IDLE_POLL_INTERVAL_MS = 10_000;
const ACTIVE_POLL_INTERVAL_MS = 1_000;

/**
 * Polls for clients editing the content described by `input`,
 * excluding our own client and de-duping the same user across tabs.
 *
 * Pass `null` to skip the query (e.g. when the editing context isn't
 * ready).
 *
 * Poll cadence auto-adjusts: slow (10s) when nobody else is present,
 * fast (1s) once at least one other client shows up — snappier updates
 * during active collaboration, cheaper cost when idle. Both cadences
 * pause when the tab is hidden (via `usePollInterval`).
 */
const useActiveClientsCore = (
  input: ActiveClientsInput | null,
): ActiveClientFragment[] => {
  const clientIdContext = useContext(ClientIdContext);
  const [hasActiveClients, setHasActiveClients] = useState(false);
  const pollInterval = usePollInterval(
    hasActiveClients ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS,
  );
  const { data } = useActiveClientsQuery({
    pollInterval,
    skip: !input,
    variables: { input: input ?? {} },
  });
  const activeClients = useMemo(
    () =>
      uniqBy(
        (data?.ActiveClients ?? []).filter(
          (c) => c.clientId !== clientIdContext?.clientId,
        ),
        (c) => c.user.id,
      ),
    [data?.ActiveClients, clientIdContext?.clientId],
  );
  useEffect(() => {
    setHasActiveClients(activeClients.length > 0);
  }, [activeClients.length]);
  return activeClients;
};

export default useActiveClientsCore;

gql`
  query ActiveClients($input: ActiveClientsInput!) {
    ActiveClients: AdminActiveClients(input: $input) {
      ...ActiveClient
    }
  }
`;
